/* Global SSE listener for the currently-authenticated user.
 *
 * Emits browser CustomEvents:
 *   - 'atelier:notification' with detail = notification doc
 *   - 'atelier:chat_message'  with detail = { channel_id, message }
 *
 * Components subscribe with `window.addEventListener(...)`.
 */
import { useEffect, useRef } from "react";
import { API } from "./api";
import { useAuth } from "./auth";
import { log } from "./log";

export function useEventStream() {
  const { user } = useAuth();
  const esRef = useRef(null);

  useEffect(() => {
    if (!user) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }
    let backoffMs = 1500;
    let closed = false;

    const connect = () => {
      if (closed) return;
      // Cookies (access_token httpOnly) travel via withCredentials — no token in URL.
      const url = `${API}/stream`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.addEventListener("ready", () => { backoffMs = 1500; });

      es.addEventListener("notification", (e) => {
        try {
          const data = JSON.parse(e.data);
          window.dispatchEvent(new CustomEvent("atelier:notification", { detail: data }));
        } catch (err) {
          log.warn("[SSE] Failed to parse notification event", err);
        }
      });

      es.addEventListener("chat_message", (e) => {
        try {
          const data = JSON.parse(e.data);
          window.dispatchEvent(new CustomEvent("atelier:chat_message", { detail: data }));
        } catch (err) {
          log.warn("[SSE] Failed to parse chat_message event", err);
        }
      });

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (closed) return;
        setTimeout(connect, Math.min(backoffMs, 20000));
        backoffMs = Math.min(backoffMs * 2, 20000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (esRef.current) esRef.current.close();
      esRef.current = null;
    };
  }, [user]);
}
